import React from 'react';
import * as Inovx from '@shared';
import ViewerActions from '../../actions/ViewerActions';
import ViewerStore from '../../stores/ViewerStore';
import FacilityActions from '../../actions/FacilityActions';
import FacilityStore from '../../stores/FacilityStore';
import FacilityMiniMap from '../FacilityMiniMap';
import KnowledgeLibraryOntologyFilter from '../knowledgeLibrary/KnowledgeLibraryOntologyFilter';
import ComponentHelpers from '../../helpers/ComponentHelpers';
import MediaGalleryGrid, { MediaGalleryRow } from './MediaGalleryGrid';
import MediaGalleryBulkSelectionToolbar from './MediaGalleryBulkSelectionToolbar';
import MediaGalleryThumbnail from './MediaGalleryThumbnail';
import { getViewMediaKind, toMediaGalleryHeadingDegrees } from '../../helpers/viewMedia';

const SystemActions = Inovx.Shared.System.Actions;

interface MediaGalleryProps {
    openViewHandler?: (view: IView360Detail, index: number) => void;
}

type MediaGalleryViewMode = 'thumbnail' | 'grid';
type MediaGallerySourceKey = 'scanPositions' | 'image360';

interface MediaGalleryState {
    selectedFacility: IFacilityDetail | null;
    mediaGalleryViewMode: MediaGalleryViewMode;
}

interface MediaGalleryBatchEditorState {
    visible: boolean;
    up: string;
    angle: string;
    error: string;
}

interface ViewerMiniMapState {
    showMap: boolean;
    is3DViewerVisible: boolean;
    isPanoViewerVisible: boolean;
    isPointViewerVisible: boolean;
}

const initialBatchEditorState: MediaGalleryBatchEditorState = {
    visible: false,
    up: '',
    angle: '',
    error: ''
};

function getStoreState(): MediaGalleryState {
    return {
        selectedFacility: FacilityStore.getSelectedFacility(),
        mediaGalleryViewMode: FacilityStore.getMediaGalleryViewMode()
    };
}

const matchesSearchText = (view: IView360Detail, searchText: string) => {
    if (!searchText) {
        return true;
    }

    return view.Name?.toLowerCase().includes(searchText.toLowerCase());
};

const getViewerMiniMapState = (): ViewerMiniMapState => ({
    showMap: ViewerStore.isMiniMapOpen(),
    is3DViewerVisible: ViewerStore.is3DViewerVisible(),
    isPanoViewerVisible: ViewerStore.isPanoViewerVisible(),
    isPointViewerVisible: ViewerStore.isOpenPointCloud(),
});

const MediaGallery: React.FC<MediaGalleryProps> = ({ openViewHandler }) => {
    const mountedRef = React.useRef(true);
    const [state, setState] = React.useState<MediaGalleryState>(getStoreState);
    const [viewerMiniMapState, setViewerMiniMapState] = React.useState<ViewerMiniMapState>(getViewerMiniMapState);
    const [selectedOntologyId, setSelectedOntologyId] = React.useState<string>();
    const [selectedViewId, setSelectedViewId] = React.useState<string>();
    const [selectedMediaIdSet, setSelectedMediaIdSet] = React.useState<Set<string>>(() => new Set());
    const [sourceFilters, setSourceFilters] = React.useState<Record<MediaGallerySourceKey, boolean>>({
        scanPositions: true,
        image360: true
    });
    const [searchText, setSearchText] = React.useState('');
    const [batchEditorState, setBatchEditorState] = React.useState<MediaGalleryBatchEditorState>(initialBatchEditorState);
    const [facilityTitle] = React.useState<string>(FacilityStore.getSelectedFacility().Name);
    const hasMediaGalleryAccess = React.useMemo(() => ComponentHelpers.hasFacilityExplorerPermission(), []);
    const hasModifyMediaFilesPermission = React.useMemo(() => ComponentHelpers.hasModifyMediaFilesPermission(), []);
    const hasManageMediaFilesPermission = React.useMemo(() => ComponentHelpers.hasManageMediaFilesPermission(), []);

    React.useEffect(() => {
        const handleStoreChange = () => {
            if (mountedRef.current) {
                setState(getStoreState());
            }
        };

        FacilityStore.addChangeListener(handleStoreChange);
        return () => {
            mountedRef.current = false;
            FacilityStore.removeChangeListener(handleStoreChange);
        };
    }, []);

    React.useEffect(() => {
        if (!hasMediaGalleryAccess) {
            FacilityActions.closeMediaGallery();
        }
    }, [hasMediaGalleryAccess]);

    React.useEffect(() => {
        const handleViewerStoreChange = () => {
            if (mountedRef.current) {
                setViewerMiniMapState(getViewerMiniMapState());
            }
        };

        ViewerStore.addChangeListener(handleViewerStoreChange);
        return () => {
            ViewerStore.removeChangeListener(handleViewerStoreChange);
        };
    }, []);

    const selectedFacility = state.selectedFacility;
    const viewMode = state.mediaGalleryViewMode;
    const selectedView = React.useMemo(() => {
        return selectedFacility?.Views?.find(view => view.ID === selectedViewId) ?? null;
    }, [selectedFacility, selectedViewId]);

    const facilityHierarchy = React.useMemo(() => {
        if (!selectedFacility?.OntologyParts?.length) {
            return null;
        }

        return FacilityStore.getFacilityHierarchy();
    }, [selectedFacility]);

    const siteMap = React.useMemo(() => {
        const ontology = FacilityStore.getSelectedOntology() ?? FacilityStore.getRootOntologyPart();
        return ontology?.Map;
    }, [selectedFacility]);

    const ontologyHierarchy = React.useMemo(() => {
        if (!selectedFacility?.OntologyParts?.length) {
            return {} as IOntologyHierarchyMap;
        }

        return FacilityStore.getOntologyBoundsContext().ontologyHierarchy;
    }, [selectedFacility]);

    React.useEffect(() => {
        if (!selectedOntologyId) {
            return;
        }

        const ontologyExists = selectedFacility?.OntologyParts?.some(part => part.ID === selectedOntologyId);
        if (!ontologyExists) {
            setSelectedOntologyId(undefined);
        }
    }, [selectedFacility, selectedOntologyId]);

    const getOntologyAndDescendantIds = React.useCallback((ontologyId: string): Set<string> => {
        const applicableIds = new Set<string>();
        const stack = [ontologyId];

        while (stack.length > 0) {
            const currentId = stack.pop();
            if (!currentId || applicableIds.has(currentId)) {
                continue;
            }

            applicableIds.add(currentId);
            const childIds = ontologyHierarchy[currentId]?.childIds || [];
            childIds.forEach(childId => stack.push(childId));
        }

        return applicableIds;
    }, [ontologyHierarchy]);

    const filteredViews = React.useMemo(() => {
        const views = selectedFacility?.Views || [];
        const applicableOntologyIds = selectedOntologyId ? getOntologyAndDescendantIds(selectedOntologyId) : null;

        return views
            .filter(view => !applicableOntologyIds || applicableOntologyIds.has(view.OntologyID))
            .filter(view => sourceFilters[getViewMediaKind(view)])
            .filter(view => matchesSearchText(view, searchText))
            .sort((left, right) => left.Name.localeCompare(right.Name));
    }, [getOntologyAndDescendantIds, searchText, selectedFacility, selectedOntologyId, sourceFilters]);

    const sourceCounts = React.useMemo(() => {
        const views = selectedFacility?.Views || [];

        return views.reduce<Record<MediaGallerySourceKey, number>>((accumulator, view) => {
            const sourceKey = getViewMediaKind(view);
            accumulator[sourceKey] += 1;
            return accumulator;
        }, {
            scanPositions: 0,
            image360: 0
        });
    }, [selectedFacility]);

    const galleryRows = React.useMemo<MediaGalleryRow[]>(() => {
        return filteredViews.map(view => ({
            ID: view.ID,
            Name: view.Name,
            OntologyID: view.OntologyID,
            ThumbnailImageUrl: view.ThumbnailImageUrl,
            North: view.Position?.Z ?? 0,
            East: view.Position?.X ?? 0,
            Up: view.Position?.Y ?? 0,
            Angle: toMediaGalleryHeadingDegrees(view),
            MediaKind: getViewMediaKind(view),
            View: view
        }));
    }, [filteredViews]);

    const selectedMediaRows = React.useMemo(() => {
        if (selectedMediaIdSet.size === 0) {
            return [] as MediaGalleryRow[];
        }

        return galleryRows.filter(row => selectedMediaIdSet.has(row.ID));
    }, [galleryRows, selectedMediaIdSet]);

    const selectedCount = selectedMediaIdSet.size;
    const allRowsSelected = galleryRows.length > 0 && galleryRows.every(row => selectedMediaIdSet.has(row.ID));
    const hasSomeRowsSelected = galleryRows.some(row => selectedMediaIdSet.has(row.ID));
    const canUpdateSelectedViewsMetadata = hasModifyMediaFilesPermission
        && selectedMediaRows.length > 0
        && selectedMediaRows.every(row => row.MediaKind === 'image360');
    const canSetSelectedPanoColorType = hasModifyMediaFilesPermission
        && selectedMediaRows.length > 0
        && selectedMediaRows.every(row => row.MediaKind === 'scanPositions' && row.View.PanoColorType == null);
    const canDeleteSelectedViews = hasManageMediaFilesPermission && selectedMediaRows.length > 0;

    React.useEffect(() => {
        if (selectedViewId && !filteredViews.some(view => view.ID === selectedViewId)) {
            setSelectedViewId(undefined);
        }
    }, [filteredViews, selectedViewId]);

    React.useEffect(() => {
        const visibleMediaIdSet = new Set(galleryRows.map(row => row.ID));
        setSelectedMediaIdSet(previous => {
            let changed = false;
            const next = new Set<string>();

            previous.forEach(id => {
                if (visibleMediaIdSet.has(id)) {
                    next.add(id);
                } else {
                    changed = true;
                }
            });

            return changed ? next : previous;
        });
    }, [galleryRows]);

    const handleOpenView = React.useCallback((view: IView360Detail) => {
        const viewIndex = selectedFacility?.Views?.findIndex(item => item.ID === view.ID) ?? -1;
        setSelectedViewId(view.ID);
        openViewHandler?.(view, viewIndex);
    }, [openViewHandler, selectedFacility]);

    const handleSelectView = React.useCallback((view: IView360Detail | null, mediaKind?: 'scanPositions' | 'image360') => {
        if (mediaKind === 'scanPositions') {
            setSelectedViewId(view?.ID);
        }
    }, []);

    const handleToggleMediaSelection = React.useCallback((mediaId: string) => {
        setSelectedMediaIdSet(previous => {
            const next = new Set(previous);
            if (next.has(mediaId)) {
                next.delete(mediaId);
            } else {
                next.add(mediaId);
            }

            return next;
        });
    }, []);

    const clearMediaSelection = React.useCallback(() => {
        setSelectedMediaIdSet(new Set());
    }, []);

    const handleToggleSelectAllMedia = React.useCallback(() => {
        setSelectedMediaIdSet(previous => {
            const next = new Set(previous);

            if (allRowsSelected) {
                galleryRows.forEach(row => next.delete(row.ID));
                return next;
            }

            galleryRows.forEach(row => next.add(row.ID));
            return next;
        });
    }, [allRowsSelected, galleryRows]);

    const handleUpdateViewMetadata = React.useCallback(async (update: IView360MetadataUpdate, mediaKind?: 'scanPositions' | 'image360') => {
        if (!hasModifyMediaFilesPermission) {
            return;
        }

        await FacilityActions.updateView360Metadata(update);
        if (mediaKind === 'scanPositions') setSelectedViewId(update.ID);
    }, [hasModifyMediaFilesPermission]);

    const handleUpdateViewsMetadata = React.useCallback(async (update: IView360BatchMetadataUpdate) => {
        if (!hasModifyMediaFilesPermission) {
            return;
        }

        await FacilityActions.updateView360MetadataBatch(update);
        // setSelectedViewId(update.Items[0]?.ID);
    }, [hasModifyMediaFilesPermission]);

    const handleDeleteViews = React.useCallback(async (items: IView360Identity[]) => {
        if (!hasManageMediaFilesPermission) {
            return false;
        }

        return await new Promise<boolean>((resolve) => {
            SystemActions.displayConfirmationMessage({
                message: InovxLoader.t('facility:mediaGallery.confirmDeleteMediaFiles', { count: items.length }),
                confirmCallback: async () => {
                    await FacilityActions.deleteView360Many(items);
                    setSelectedViewId(current => items.some(item => item.ID === current) ? undefined : current);
                    resolve(true);
                },
                cancelCallback: () => resolve(false)
            });
        });
    }, [hasManageMediaFilesPermission]);

    const handleDeleteSelectedViews = React.useCallback(async () => {
        if (selectedMediaRows.length === 0) {
            return;
        }

        const deleted = await handleDeleteViews(selectedMediaRows.map(row => ({
            ID: row.View.ID,
            VersionToken: row.View.VersionToken
        })));

        if (deleted) {
            clearMediaSelection();
        }
    }, [clearMediaSelection, handleDeleteViews, selectedMediaRows]);

    const handleSetSelectedPanoColorType = React.useCallback(async (panoColorType: PanoColorType) => {
        if (!canSetSelectedPanoColorType) {
            return;
        }

        await handleUpdateViewsMetadata({
            Items: selectedMediaRows.map(row => ({ ID: row.View.ID, VersionToken: row.View.VersionToken })),
            PanoColorType: panoColorType
        });
    }, [canSetSelectedPanoColorType, handleUpdateViewsMetadata, selectedMediaRows]);

    const handleMarkSelectedColor = React.useCallback(async () => {
        await handleSetSelectedPanoColorType(0);
    }, [handleSetSelectedPanoColorType]);

    const handleMarkSelectedBlackAndWhite = React.useCallback(async () => {
        await handleSetSelectedPanoColorType(1);
    }, [handleSetSelectedPanoColorType]);

    const handleOpenBatchEditor = React.useCallback(() => {
        if (!canUpdateSelectedViewsMetadata) {
            return;
        }

        setBatchEditorState({
            visible: true,
            up: '',
            angle: '',
            error: ''
        });
    }, [canUpdateSelectedViewsMetadata]);

    const closeBatchEditor = React.useCallback(() => {
        setBatchEditorState(initialBatchEditorState);
    }, []);

    const handleBatchEditorSubmit = React.useCallback(async () => {
        const trimmedUp = batchEditorState.up.trim();
        const trimmedAngle = batchEditorState.angle.trim();

        if (!trimmedUp && !trimmedAngle) {
            setBatchEditorState(current => ({ ...current, error: InovxLoader.t('facility:mediaGallery.enterUpOrAngle') }));
            return;
        }

        const parsedUp = trimmedUp ? Number(trimmedUp) : null;
        const parsedAngle = trimmedAngle ? Number(trimmedAngle) : null;

        if ((trimmedUp && Number.isNaN(parsedUp)) || (trimmedAngle && Number.isNaN(parsedAngle))) {
            setBatchEditorState(current => ({ ...current, error: InovxLoader.t('facility:mediaGallery.enterValidNumbers') }));
            return;
        }

        if (parsedAngle != null && (parsedAngle < 0 || parsedAngle > 360)) {
            setBatchEditorState(current => ({ ...current, error: InovxLoader.t('facility:mediaGallery.angleRange') }));
            return;
        }

        await handleUpdateViewsMetadata({
            Items: selectedMediaRows.map(row => ({ ID: row.View.ID, VersionToken: row.View.VersionToken })),
            Up: parsedUp,
            Angle: parsedAngle
        });

        closeBatchEditor();
    }, [batchEditorState.angle, batchEditorState.up, closeBatchEditor, handleUpdateViewsMetadata, selectedMediaRows]);

    const thumbnailSelectionToolbar = React.useMemo(() => (
        <MediaGalleryBulkSelectionToolbar
            key={selectedCount}
            selectedCount={selectedCount}
            canUpdateMetadata={canUpdateSelectedViewsMetadata}
            canSetPanoColorType={canSetSelectedPanoColorType}
            canDelete={canDeleteSelectedViews}
            onClearSelection={clearMediaSelection}
            onUpdateMetadata={handleOpenBatchEditor}
            onMarkColor={handleMarkSelectedColor}
            onMarkBlackAndWhite={handleMarkSelectedBlackAndWhite}
            onDeleteMedia={handleDeleteSelectedViews}
        />
    ), [canDeleteSelectedViews, canSetSelectedPanoColorType, canUpdateSelectedViewsMetadata, clearMediaSelection, handleDeleteSelectedViews, handleMarkSelectedBlackAndWhite, handleMarkSelectedColor, handleOpenBatchEditor, selectedCount]);

    const handleMiniMapViewClicked = React.useCallback((view: IView360Detail) => {
        handleOpenView(view);
    }, [handleOpenView]);

    const emptyMessage = InovxLoader.t(searchText || selectedOntologyId
        ? 'facility:mediaGallery.noMatchingMedia'
        : 'facility:mediaGallery.noMediaAvailable');

    const toggleSourceFilter = React.useCallback((sourceKey: MediaGallerySourceKey) => {
        setSourceFilters(previous => ({
            ...previous,
            [sourceKey]: !previous[sourceKey]
        }));
    }, []);

    if (!hasMediaGalleryAccess) {
        return null;
    }

    return (
        <div id="media-gallery" className="media-gallery-page minimap-container">
            <div className="kl-main-container d-flex j-cc" >
                <div className="kv-container">
                    <div className="d-flex j-csb my-3 media-gallery-heading-row">
                        <div className="page-title">{InovxLoader.t('facility:menu.mediaGallery')}</div>
                        <div className="media-gallery-view-toggle" role="group" aria-label={InovxLoader.t('facility:mediaGallery.layout')}>
                            <button
                                type="button"
                                className={viewMode === 'thumbnail' ? 'e-btn e-flat kb-filter-toggle active media-gallery-layout-toggle' : 'e-btn e-flat kb-filter-toggle media-gallery-layout-toggle'}
                                title={InovxLoader.t('facility:mediaGallery.thumbnailLayout')}
                                onClick={() => FacilityActions.setMediaGalleryViewMode('thumbnail')}
                            >
                                <span className="toggle-icon card-view"></span>
                            </button>
                            <button
                                type="button"
                                className={viewMode === 'grid' ? 'e-btn e-flat kb-filter-toggle active media-gallery-layout-toggle' : 'e-btn e-flat kb-filter-toggle media-gallery-layout-toggle'}
                                title={InovxLoader.t('facility:mediaGallery.gridLayout')}
                                onClick={() => FacilityActions.setMediaGalleryViewMode('grid')}
                            >
                                <span className="toggle-icon list-view"></span>
                            </button>
                        </div>
                    </div>
                    <Inovx.Shared.Core.ReactLoader appReady={true}>
                        <div className="kb-library-app-content media-gallery-app-content">
                            <div className="media-gallery-toolbar e-grid">
                                <div className="media-gallery-toolbar-section media-gallery-toolbar-section-left">
                                    <button
                                        type="button"
                                        className={sourceFilters.scanPositions ? 'e-btn e-flat media-gallery-filter-toggle active media-gallery-source-filter' : 'e-btn e-flat media-gallery-filter-toggle media-gallery-source-filter'}
                                        title={InovxLoader.t('facility:menu.scanPositions')}
                                        onClick={() => toggleSourceFilter('scanPositions')}
                                    >
                                        <span className="media-gallery-type-icon media-gallery-type-icon-scan" aria-hidden="true"></span>
                                        <span className="media-gallery-filter-toggle-label">{InovxLoader.t('facility:menu.scanPositions')}</span>
                                        <span className="media-gallery-filter-count">{sourceCounts.scanPositions}</span>
                                    </button>
                                    <button
                                        type="button"
                                        className={sourceFilters.image360 ? 'e-btn e-flat media-gallery-filter-toggle active media-gallery-source-filter' : 'e-btn e-flat media-gallery-filter-toggle media-gallery-source-filter'}
                                        title={InovxLoader.t('facility:menu.image360')}
                                        onClick={() => toggleSourceFilter('image360')}
                                    >
                                        <span className="media-gallery-type-icon media-gallery-type-icon-image" aria-hidden="true"></span>
                                        <span className="media-gallery-filter-toggle-label">{InovxLoader.t('facility:menu.image360')}</span>
                                        <span className="media-gallery-filter-count">{sourceCounts.image360}</span>
                                    </button>
                                </div>
                                <div className="media-gallery-toolbar-section media-gallery-toolbar-section-right">
                                    {facilityHierarchy && (
                                        <div className="kb-ontology-filter media-gallery-ontology-filter">
                                            <KnowledgeLibraryOntologyFilter
                                                facilityHierarchy={facilityHierarchy}
                                                selectedOntologyId={selectedOntologyId}
                                                onChange={setSelectedOntologyId}
                                            />
                                        </div>
                                    )}
                                    <div className="media-gallery-search-group e-input-group e-control-wrapper">
                                        <input
                                            type="text"
                                            className="e-input"
                                            placeholder={InovxLoader.t('facility:menu.search')}
                                            value={searchText}
                                            onChange={(event) => setSearchText(event.target.value)}
                                        />
                                        {searchText && (
                                            <button
                                                type="button"
                                                className="media-gallery-search-clear"
                                                title={InovxLoader.t('common:clear')}
                                                aria-label={InovxLoader.t('common:clear')}
                                                onClick={() => setSearchText('')}
                                            >
                                                <span className="e-icons e-close" aria-hidden="true"></span>
                                            </button>
                                        )}
                                        <span className="e-input-group-icon e-search-icon e-icons" aria-hidden="true"></span>
                                    </div>

                                </div>
                            </div>

                            {galleryRows.length === 0 ? (
                                <div className="media-gallery-empty-state">{emptyMessage}</div>
                            ) : viewMode === 'grid' ? (
                                <MediaGalleryGrid
                                    rows={galleryRows}
                                    selectedMediaIdSet={selectedMediaIdSet}
                                    selectedCount={selectedCount}
                                    allRowsSelected={allRowsSelected}
                                    hasSomeRowsSelected={hasSomeRowsSelected}
                                    onOpenView={handleOpenView}
                                    onSelectView={handleSelectView}
                                    onToggleMediaSelection={handleToggleMediaSelection}
                                    onToggleSelectAllMedia={handleToggleSelectAllMedia}
                                    onClearSelection={clearMediaSelection}
                                    onOpenBatchEditor={handleOpenBatchEditor}
                                    onMarkSelectedColor={handleMarkSelectedColor}
                                    onMarkSelectedBlackAndWhite={handleMarkSelectedBlackAndWhite}
                                    onDeleteSelectedViews={handleDeleteSelectedViews}
                                    canUpdateSelectedViewsMetadata={canUpdateSelectedViewsMetadata}
                                    canSetSelectedPanoColorType={canSetSelectedPanoColorType}
                                    canDeleteSelectedViews={canDeleteSelectedViews}
                                />
                            ) : (
                                <MediaGalleryThumbnail
                                    rows={galleryRows}
                                    selectedMediaIdSet={selectedMediaIdSet}
                                    selectionToolbar={thumbnailSelectionToolbar}
                                    onOpenView={handleOpenView}
                                    onSelectView={(view, mediaKind) => handleSelectView(view, mediaKind)}
                                    onToggleMediaSelection={handleToggleMediaSelection}
                                    onUpdateViewMetadata={handleUpdateViewMetadata}
                                    canModifyMediaFiles={hasModifyMediaFilesPermission}
                                />
                            )}
                        </div>
                    </Inovx.Shared.Core.ReactLoader>
                </div>
                {batchEditorState.visible && (
                    <div className="media-gallery-batch-editor-backdrop" role="presentation">
                        <div className="media-gallery-batch-editor-dialog" role="dialog" aria-modal="true" aria-labelledby="media-gallery-batch-editor-title">
                            <div className="media-gallery-batch-editor-header">
                                <div id="media-gallery-batch-editor-title" className="font-bold">{InovxLoader.t('facility:mediaGallery.batchUpdateTitle')}</div>
                            </div>
                            <div className="media-gallery-batch-editor-body">
                                <div className="media-gallery-batch-editor-field">
                                    <label htmlFor="media-gallery-batch-up">{InovxLoader.t('facility:mediaGallery.upMeters')}</label>
                                    <input
                                        id="media-gallery-batch-up"
                                        type="number"
                                        className="e-input"
                                        value={batchEditorState.up}
                                        onChange={(event) => setBatchEditorState(current => ({ ...current, up: event.target.value, error: '' }))}
                                    />
                                </div>
                                <div className="media-gallery-batch-editor-field">
                                    <label htmlFor="media-gallery-batch-angle">{InovxLoader.t('facility:mediaGallery.angleDegrees')}</label>
                                    <input
                                        id="media-gallery-batch-angle"
                                        type="number"
                                        min={0}
                                        max={360}
                                        className="e-input"
                                        value={batchEditorState.angle}
                                        onChange={(event) => setBatchEditorState(current => ({ ...current, angle: event.target.value, error: '' }))}
                                    />
                                </div>
                                <div className="media-gallery-batch-editor-help">{InovxLoader.t('facility:mediaGallery.batchUpdateHelp')}</div>
                                {batchEditorState.error && <div className="media-gallery-batch-editor-error">{batchEditorState.error}</div>}
                            </div>
                            <div className="media-gallery-batch-editor-actions">
                                <button type="button" className="e-btn e-primary" onClick={handleBatchEditorSubmit}>{InovxLoader.t('facility:mediaGallery.update')}</button>
                                <button type="button" className="e-btn e-flat" onClick={closeBatchEditor}>{InovxLoader.t('common:form.cancel')}</button>
                            </div>
                        </div>
                    </div>
                )}
                {viewerMiniMapState.showMap && !viewerMiniMapState.is3DViewerVisible && !viewerMiniMapState.isPanoViewerVisible && !viewerMiniMapState.isPointViewerVisible && (
                    <FacilityMiniMap
                        siteMap={siteMap}
                        views={filteredViews}
                        title={facilityTitle}
                        targetId="#media-gallery"
                        activeView={selectedView}
                        highlightedView={selectedView}
                        viewerDirectionDegrees={selectedView ? toMediaGalleryHeadingDegrees(selectedView) ?? 0 : 0}
                        knowledgeViewOpened={false}
                        pointCloudAvailable={false}
                        onViewMarkerClicked={handleMiniMapViewClicked}
                        onViewMarkerHighlighted={(view, _index, highlighted) => handleSelectView(highlighted ? view : selectedView)}
                    />
                )}
            </div>
        </div>
    );
};

export default MediaGallery;
